const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;
const UPLOAD_TIMEOUT_MS = 120_000; // 2 minutes

const delay = (ms: number) => new Promise((resolve) => { setTimeout(resolve, ms); });

const isRetryable = (error: unknown): boolean => {
  if (error instanceof TypeError) return true; // network failure per fetch spec
  if (error instanceof DOMException && error.name === 'AbortError') return true;
  return false;
};

const uploadAttempt = async (
  presignedUrl: string,
  data: File,
  attempt: number,
): Promise<void> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);

  try {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: data,
      headers: { 'Content-Type': data.type },
      signal: controller.signal,
    });

    if (response.ok) {
      return;
    }

    if (response.status >= 500 && attempt < MAX_RETRIES) {
      await delay(BASE_DELAY_MS * 2 ** attempt);
      await uploadAttempt(presignedUrl, data, attempt + 1);
      return;
    }

    throw new Error(`Error uploading file: ${response.statusText}`);
  } catch (error) {
    if (attempt < MAX_RETRIES && isRetryable(error)) {
      await delay(BASE_DELAY_MS * 2 ** attempt);
      await uploadAttempt(presignedUrl, data, attempt + 1);
      return;
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Upload a file to an S3 bucket using a presigned URL.
 *
 * Retries up to MAX_RETRIES times with exponential backoff on transient
 * failures (network errors, S3 5xx). Each attempt is capped by a timeout
 * via AbortController to avoid indefinitely hanging uploads.
 */
const putS3Document = async (presignedUrl: string, data: File) => {
  await uploadAttempt(presignedUrl, data, 0);
};

export default putS3Document;
